import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { FieldType, IField } from '@sla-ng/ui-material';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IContributionMarginLevel } from '../../../models/contribution-margin-level.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostTypeCostCenterDistribution } from '../../../models/cost-type-cost-center-distribution.model';
import { ICostType } from '../../../models/cost-type.model';
import { ICostingLevel } from '../../../models/costing-level.model';
import { IReference } from '../../../models/reference.model';
import { ContributionMarginLevelResource } from './../../../resources/contribution-margin-level.resource';
import { CostCenterResource } from './../../../resources/cost-center.resource';
import { CostTypeResource } from './../../../resources/cost-type.resource';
import { CostingLevelResource } from './../../../resources/costing-level.resource';
import { ReferenceResource } from './../../../resources/reference.resource';
import { IJoinCostTypeCostCenterState } from './join-cost-type-cost-center-state.model';

let _state: IJoinCostTypeCostCenterState = {
    contributionMarginLevelLookupMap: {}, costingLevelLookupMap: {},
    distributions: [], costTypes: [], selectedCostType: null, costCenterLookupMap: {},
    costCenters: [],
    contributionMarginLevels: [],
    references: [],
    costingLevels: [],
    columns: [ 'costCenter', 'contributionMarginLevel', 'costingLevel', 'type', 'actions' ],
    isLoading: false,
    addFields: [],
    editFields: [],
    referenceSelectInput: {
        field: 'distributionReference',
        type: FieldType.select,
        label: 'app.model.cost-type.distribution-reference.label',
        selectData: {
            displaySearch: true,
            labelKeys: [ 'name' ],
            valueKey: 'id',
            options: of([]),
        },
    },
};

@Injectable({
    providedIn: 'root',
})
export class JoinCostTypeCostCenterFacade {

    protected store$: BehaviorSubject<IJoinCostTypeCostCenterState> = new BehaviorSubject<IJoinCostTypeCostCenterState>(_state);

    constructor(
        private myCostCenterResource: CostCenterResource,
        private myCostTypeResource: CostTypeResource,
        private myReferenceResource: ReferenceResource,
        private myCostingLevelResource: CostingLevelResource,
        private myContributionMarginLevelResource: ContributionMarginLevelResource,
        private myMessageService: MessageService) {
    }

    public getStateSnapshot(): IJoinCostTypeCostCenterState {
        return this.store$.value;
    }

    public async fetchData(): Promise<void> {
        this.setIsLoading(true);

        const [ costTypes, references, costingLevels, contributionMarginLevels, costCenters ]:
        [ ICostType[], IReference[], ICostingLevel[], IContributionMarginLevel[], ICostCenter[] ] = await Promise.all([
            firstValueFrom(this.myCostTypeResource.getCostTypes(false, false, false)).catch(() => []),
            firstValueFrom(this.myReferenceResource.getAllReferences()).catch(() => []),
            firstValueFrom(this.myCostingLevelResource.getAllCostingLevels()).catch(() => []),
            firstValueFrom(this.myContributionMarginLevelResource.getAllContributionMarginLevels()).catch(() => []),
            firstValueFrom(this.myCostCenterResource.getCostCenters()).catch(() => []),
        ]);

        this.createLookupMaps(costCenters, costingLevels, contributionMarginLevels);
        this.updateState({ ..._state, costTypes, costCenters, references, costingLevels, contributionMarginLevels });

        this.createReferenceSelectInput();
        this.createDialogFields();
        this.setIsLoading(false);
    }

    public subState(): Observable<IJoinCostTypeCostCenterState> {
        return this.store$.asObservable();
    }

    public async setSelectedCostType(costType: ICostType): Promise<void> {

        this.updateState({ ..._state, isLoading: true, selectedCostType: costType });
        if (costType) {
            /* Fetches all assignments of selected costType <--> costCenters*/
            await this.fetchAssignments();
            this.createDialogAddFields();
        }

        this.setIsLoading(false);

    }

    public async addCostTypeCostCenterDistribution(distribution: ICostTypeCostCenterDistribution): Promise<void> {
        this.setIsLoading(true);
        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();
        distribution.costTypeId = state.selectedCostType.id;
        const response: ICostTypeCostCenterDistribution = await firstValueFrom(this.myCostTypeResource.addCostTypeDistribution(distribution))
            .catch(() => null);
        if (response !== null) {
            const distributions: ICostTypeCostCenterDistribution[] = state.distributions;
            distributions.push(response);
            this.updateState({ ..._state, distributions });
            this.createDialogAddFields();
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type-cost-center-distribution.actions.added.label');
        }
        this.setIsLoading(false);
    }

    public async deleteCostTypeDistribution(distribution: ICostTypeCostCenterDistribution): Promise<void> {
        this.setIsLoading(true);
        try {
            await firstValueFrom(this.myCostTypeResource.deleteCostTypeDistribution(distribution)).catch(() => null);
            this.myMessageService.successTrans('app.actions.feedback.deleted', 'app.page.cost-type-cost-center-distribution.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        await this.fetchAssignments();
        this.setIsLoading(false);
    }

    public async updateCostTypeDistribution(distribution: ICostTypeCostCenterDistribution): Promise<void> {
        this.setIsLoading(true);
        distribution.costTypeId = this.getStateSnapshot().selectedCostType.id;
        const editedDistribution: ICostTypeCostCenterDistribution = await firstValueFrom(this.myCostTypeResource
            .updateCostTypeDistribution(distribution)).catch(() => null);
        if (editedDistribution !== null) {
            this.myMessageService.successTrans('app.actions.feedback.edited', 'app.page.cost-type-cost-center-distribution.actions.edited.label');
        }
        await this.fetchAssignments();
        this.setIsLoading(false);
    }

    public async setCostTypeDistributionReference(referenceId: number): Promise<void> {
        this.setIsLoading(true);
        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();
        const updatedCostType: ICostType = await firstValueFrom(this.myCostTypeResource.changeDistributionReference(state.selectedCostType, referenceId)).catch(() => null);
        if (updatedCostType !== null) {
            this.myMessageService.successTrans('app.actions.feedback.edited', 'app.page.cost-type-cost-center-distribution-reference.actions.edited.label');
            this.updateState({ ..._state, selectedCostType: updatedCostType });
            this.updateCostTypeInList(updatedCostType);
        }
        this.setIsLoading(false);

    }


    public clearState(): void {
        this.updateState({
            contributionMarginLevelLookupMap: {}, costingLevelLookupMap: {},
            distributions: [], costTypes: [], selectedCostType: null, costCenterLookupMap: {},
            costCenters: [],
            contributionMarginLevels: [],
            references: [],
            costingLevels: [],
            columns: [ 'costCenter', 'contributionMarginLevel', 'costingLevel', 'type', 'actions' ],
            isLoading: false,
            addFields: [],
            editFields: [],
            referenceSelectInput: {
                field: 'distributionReference',
                type: FieldType.select,
                label: 'app.model.cost-type.distribution-reference.label',
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of([]),
                },
            },
        });
    }

    private updateState(state: IJoinCostTypeCostCenterState): void {
        this.store$.next(_state = state);
    }

    private async fetchAssignments(): Promise<void> {
        this.setIsLoading(true);
        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();
        const distributions: ICostTypeCostCenterDistribution[] = await firstValueFrom(this.myCostTypeResource.getCostTypeDistributions(state.selectedCostType)).catch(() => []);
        this.updateState({ ..._state, distributions });
        this.createDialogAddFields();
        this.setIsLoading(false);
    }

    private createDialogFields(): void {
        this.createDialogAddFields();

        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();

        const dialogFields: IField[] = [
            {
                field: 'costCenterId',
                label: 'app.model.cost-type-distribution.cost-center.label',
                type: FieldType.select,
                disabled: true,
                readonly: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(state.costCenters),
                },
                required: true,
            },
            {
                field: 'costingLevelId',
                label: 'app.model.cost-type-distribution.costing-level.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(state.costingLevels).pipe(map(costingLevels => costingLevels.filter(cL => !cL.other)))
                },
                required: true,
            },
            {
                field: 'contributionMarginLevelId',
                label: 'app.model.cost-type-distribution.contribution-margin-level.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(state.contributionMarginLevels),
                },
                required: true,
            },
            { field: 'variable', label: 'app.model.cost-type-distribution.isVariable.label', type: FieldType.checkbox },
        ];
        this.updateState({ ..._state, editFields: dialogFields });
    }

    private createDialogAddFields(): void {
        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();

        // Only allow selection of costCenters that are not assigned yet
        const unassignedCostCenters: ICostCenter[] = state.costCenters.filter(costCenter => {
            return !state.distributions.some(distribution => distribution.costCenterId === costCenter.id);
        });

        const dialogFields: IField[] = [
            {
                field: 'costCenterId',
                label: 'app.model.cost-type-distribution.cost-center.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(unassignedCostCenters),
                },
                required: true,
            },
            {
                field: 'costingLevelId',
                label: 'app.model.cost-type-distribution.costing-level.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(state.costingLevels).pipe(map(costingLevels => costingLevels.filter(cL => !cL.other)))
                },
                required: true,
            },
            {
                field: 'contributionMarginLevelId',
                label: 'app.model.cost-type-distribution.contribution-margin-level.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(state.contributionMarginLevels),
                },
                required: true,
            },
            { field: 'variable', label: 'app.model.cost-type-distribution.isVariable.label', type: FieldType.checkbox },
        ];
        this.updateState({ ..._state, addFields: dialogFields });
    }

    private createReferenceSelectInput(): void {
        const state: IJoinCostTypeCostCenterState = this.getStateSnapshot();
        const referenceSelectInput: IField = state.referenceSelectInput;
        referenceSelectInput.selectData.options = of(state.references);
        this.updateState({ ..._state, referenceSelectInput });
    }

    private createLookupMaps(costCenters: ICostCenter[], costingLevels: ICostingLevel[], contributionMarginLevels: IContributionMarginLevel[]): void {
        const costCenterLookupMap: Record<number, ICostCenter> = {};
        const costingLevelLookupMap: Record<number, ICostingLevel> = {};
        const contributionMarginLevelLookupMap: Record<number, IContributionMarginLevel> = {};

        for (const costCenter of costCenters) {
            costCenterLookupMap[ costCenter.id ] = costCenter;
        }
        for (const costingLevel of costingLevels) {
            costingLevelLookupMap[ costingLevel.id ] = costingLevel;
        }
        for (const contributionMarginLevel of contributionMarginLevels) {
            contributionMarginLevelLookupMap[ contributionMarginLevel.id ] = contributionMarginLevel;
        }
        this.updateState({ ..._state, costCenterLookupMap });
        this.updateState({ ..._state, costingLevelLookupMap });
        this.updateState({ ..._state, contributionMarginLevelLookupMap });
    }

    private setIsLoading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private updateCostTypeInList(updatedCostType: ICostType): void {
        const costTypes: ICostType[] = this.getStateSnapshot().costTypes;
        const index: number = costTypes.findIndex((costType) => costType.id === updatedCostType.id);
        if (index !== -1) {
            costTypes[ index ] = updatedCostType;
            this.updateState({ ..._state, costTypes });
        }
    }
}
